import { Flows, Flow, Step } from "./constants";
import currency from "currency.js";
import { DateTime } from "luxon";
import { Enums } from "@shared";
import type { NavigateFunction } from "react-router-dom";
import type { RawSession, SessionData, Account, SelectedAccount, OrgDays } from "@app-types";

export const dayOrdinal = (d: number | null | undefined): string | null => {

    if (!d) return null;
    return d + (d === 31 || d === 21 || d === 1 ? `st` : d === 22 || d === 2 ? `nd` : d === 23 || d === 3 ? `rd` : `th`);

};

export const printPercentage = (percentage?: string | number | null): string => {
    if (percentage === null || percentage === undefined || percentage === ``) return ``;
    const fix = currency(percentage, { symbol: ``, precision: 4 }).multiply(100);
    return fix.cents() > 0 ? currency(fix, { symbol: ``, precision: 2 }).format() : currency(fix, { symbol: ``, precision: 0 }).format();
};

const findPath = (currentPath: string, steps: Step[]): number => {
    const path = currentPath.toLocaleLowerCase();
    const index = steps.findIndex(r => {
        const isArray = Array.isArray(r);
        return isArray && r.findIndex(p => p.indexOf(path) !== -1) !== -1 ||
            typeof r === `string` && r.indexOf(path) !== -1 ||
            !isArray && typeof r === `object` && r.path.indexOf(path) !== -1;
    });
    return index;
};

interface BaseLocation {
    pathname: string;
    state?: unknown;
}

interface LocationState {
    flow?: string;
    start?: string;
    overrides?: Record<string, unknown>;
}

const basePath = (location: BaseLocation, params?: Record<string, string | undefined>): string => {

    if (!params)
        return location.pathname;
    return Object.values(params).reduce<string>(
        (path, param) => param ? path.replace(`/${param}`, ``) : path,
        location.pathname
    );
};

const nextRoute = (location: BaseLocation, state: LocationState, overrides: Record<string, unknown>, params?: Record<string, string | undefined>): string | null => {
    let route: string | null = null;
    const { flow: flowName } = state;
    const flow: Flow | undefined = flowName ? Flows[flowName as keyof typeof Flows] : undefined;
    const path = basePath(location, params);

    if (!flow) throw new Error(`Flow not found. Flow name: ${flowName}`);

    const index = findPath(path, flow.steps);

    if (index === -1) throw new Error(`Next route not found.`);

    for (const nextStep of flow.steps.slice(index + 1)) {
        // Skip string[] steps (which should only be entry points, not navigation targets)
        if (Array.isArray(nextStep)) {
            continue;
        }
        // Now nextStep is navigable (string | { path: string; optional?: ... })
        if (typeof nextStep === `object` && nextStep.optional && nextStep.optional(overrides)) {
            continue;
        } else {
            route = typeof nextStep === `object` ? nextStep.path : nextStep;
            break;
        }
    }

    return route;
};

export const navigateStart = (location: BaseLocation, navigate: NavigateFunction, flow: string, newOverrides: Record<string, unknown> = {}, newState: Record<string, unknown> = {}, params?: Record<string, string | undefined>): void => {

    const overrides = { ...newOverrides };
    const state: LocationState = { ...newState, flow, overrides, start: location.pathname };
    const next = nextRoute(location, state, overrides, params);
    if (!next) return; // For TS compiler; in practice, next should always be defined
    navigate(next, { state });
};

export const navigateTo = (to: string, location: BaseLocation, navigate: NavigateFunction, newOverrides: Record<string, unknown> = {}, newState: Record<string, unknown> = {}): void => {

    let overrides: Record<string, unknown>, state: LocationState;
    if (location.state === null || location.state === undefined) {
        overrides = { ...newOverrides };
        state = { ...newState };
    } else {
        const { overrides: oldOverrides, ...rest } = location.state as LocationState & { overrides?: Record<string, unknown> };
        overrides = { ...oldOverrides, ...newOverrides };
        state = { ...rest, ...newState };
    }

    if (!state.flow) {
        console.error(`Missing flow definition`);
    }

    navigate(to, { state: { ...state, overrides: { ...overrides } } });
};

export const navigateNext = (location: BaseLocation, navigate: NavigateFunction, newOverrides: Record<string, unknown> = {}, newState: Record<string, unknown> = {}, params?: Record<string, string | undefined>): void => {

    let overrides: Record<string, unknown>, state: LocationState;
    if (location.state === null || location.state === undefined) {
        overrides = { ...newOverrides };
        state = { ...newState };
    } else {
        const { overrides: oldOverrides, ...rest } = location.state as LocationState & { overrides?: Record<string, unknown> };
        overrides = { ...oldOverrides, ...newOverrides };
        state = { ...rest, ...newState };
    }

    if (!state.flow) console.error(`Missing flow definition`);
    const finalState: LocationState = { ...state, overrides: { ...overrides } };
    const next = nextRoute(location, finalState, overrides, params);
    if (!next) return; // For TS compiler; in practice, next should always be defined
    navigate(next, { state: finalState });
};

export const compoundDay = (date: string, day: number): DateTime => DateTime.fromISO(date).startOf(`month`).set({ day: day });

export const amount = (percentage: currency.Any, total: number): number => currency(percentage, { precision: 4 }).divide(100).multiply(total).value;

export const percentage = (amountValue: currency.Any, total: number): number => currency(amountValue, { precision: 5 }).divide(total).value;

export const monthName = (date: string): string => DateTime.fromISO(date).toLocaleString({ month: `long` });

export const parseMySQLDate = (rawDate: string): Date => new Date(rawDate.replace(`-`, `/`));

export const possessive = (string: string): string => {

    if (string === ``) return string;

    const lastChar = string.slice(-1);
    const endOfWord = lastChar.toLowerCase() === `s` ? `'` : `'s`;
    return `${string}${endOfWord}`;
};

interface FeeRule {
    idFeeType: number;
    fee: number;
}

export const printFeeRule = (rule: FeeRule): string => {

    if (rule.idFeeType === Enums.FeeType.Fixed)
        return currency(rule.fee).format();

    const feePercent = rule.fee * 100;
    return feePercent % 1 === 0 ? `${feePercent}%` : `${feePercent.toFixed(1)}%`;
};

export const nullPrint = <T>(value: T | null | undefined): string | T => value ?? ``;

export const toFixedDown = (value: number, precision: number): number => {
    const re = new RegExp(`^-?\\d+(?:\\.\\d{0,${precision}})?`);
    const m = value.toString().match(re);
    return m ? currency(m[0], { precision }).value : value;
};

export const sessionData = (session: RawSession, selectedAccount: number): SessionData | null => {

    const { accounts, ...rest } = session;
    const account = accounts.find(a => a.idAccount === selectedAccount);
    if (!account) return null; // For TS compiler; selectedAccount should always exist in accounts
    return { ...rest, ...account };
};

export const getDaysFromPayor = (orgDays: OrgDays, payorAutodrawDay: number | null, idProductType: number | null = null, potDeadline: number | null = null): { maxDay: number | null; autodrawDay: number | null; locked: boolean } => {
    const { autodrawDay: orgAutodrawDay, dueDay: orgDueDay } = orgDays;

    const locked = orgDueDay === orgAutodrawDay;
    let maxDay: number | null, autodrawDay: number | null;

    if (idProductType === Enums.ProductType.SunboundPay) {
        autodrawDay = payorAutodrawDay ?? potDeadline;
        maxDay = potDeadline;
    } else {
        autodrawDay = payorAutodrawDay ?? orgAutodrawDay;
        maxDay = autodrawDay > orgAutodrawDay ? autodrawDay : orgAutodrawDay;
    }
    return { maxDay, autodrawDay, locked };
};

export const shouldResolveDefaults = (accounts: Account[], selectedAccount: SelectedAccount): boolean => {
    // we could always try to resolve PM's even if the current account is fine
    // but for now we will skip resolution if the current account is fine
    const accountsNeedingResolution = accounts.filter(a =>
        !a.paymentPreferences.defaultPaymentMethod || a.paymentPreferences.defaultPaymentMethod?.disabledAt
    );
    const selectedAccountNeedingResolution = accountsNeedingResolution.find(a => a.idAccount === selectedAccount.idAccount);
    return !!selectedAccountNeedingResolution && accountsNeedingResolution.length > 1;
};

// Like the default "format" option for currency.js, except if the cents are
// "00", then omit them.
export const formatConditionalCents: currency.Format = (currencyValue, settings) => {
    if (!currencyValue || !settings) return ``;
    const { pattern = `!#`, negativePattern = `-!#`, symbol = `$`, separator = `,`, decimal = `.` } = settings;
    const groups = /(\d)(?=(\d{3})+\b)/g;
    const split = (`` + currencyValue).replace(/^-/, ``).split(`.`);
    const dollars = split[0] ?? ``;
    const cents = split[1] ?? ``;
    const nonZeroCents = Number(cents) !== 0;
    return (currencyValue.value >= 0 ? pattern : negativePattern).replace(`!`, symbol).replace(`#`, dollars.replace(groups, `$1` + separator) + (nonZeroCents ? decimal + cents : ``));
};

export const getCommunitySubdomain = (communityShortName: string, location: Pick<Location, `hostname` | `port`> = window.location): string => {
    const currentHostname = location.hostname;
    const parts = currentHostname.split(`.`);

    let baseDomain: string;

    // 1-part domains (e.g., localhost) are treated as base domains
    if (parts.length === 1) {
        baseDomain = currentHostname;
    } else if (parts[0] === `qa`) {
        // Domains with "qa" (e.g., qa.sunboundhomes.com) are treated as bare base domains
        baseDomain = currentHostname;
    } else if (currentHostname === `lvh.me`) {
        // lvh.me is a dev alias (*.lvh.me resolves to 127.0.0.1) — treat as bare base domain
        baseDomain = currentHostname;
    } else {
        // All other multi-part domains: first part is subdomain, rest is base
        baseDomain = parts.slice(1).join(`.`);
    }

    // Build target hostname with community subdomain
    const targetHostname = `${communityShortName}.${baseDomain}`;

    // Preserve port if present
    return location.port ? `${targetHostname}:${location.port}` : targetHostname;
};
